import { clamp } from './statistics'

export type OddsFormat = 'decimal' | 'american' | 'fractional'

export interface Fractional {
  numerator: number
  denominator: number
}

const EPS = 1e-9

export function isValidDecimalOdds(d: number): boolean {
  return Number.isFinite(d) && d > 1 + EPS
}

export function decimalToImplied(decimalOdds: number): number {
  if (!isValidDecimalOdds(decimalOdds)) return 0
  return clamp(1 / decimalOdds, 0, 1)
}

export function impliedToDecimal(prob: number): number {
  const p = clamp(prob, EPS, 1)
  return 1 / p
}

export function americanToDecimal(american: number): number {
  if (american === 0) return 1
  return american > 0 ? american / 100 + 1 : 100 / Math.abs(american) + 1
}

export function decimalToAmerican(decimalOdds: number): number {
  if (!isValidDecimalOdds(decimalOdds)) return 0
  const b = decimalOdds - 1
  return b >= 1 ? Math.round(b * 100) : Math.round(-100 / b)
}

export function americanToImplied(american: number): number {
  return decimalToImplied(americanToDecimal(american))
}

export function fractionalToDecimal(f: Fractional): number {
  if (f.denominator === 0) return 1
  return f.numerator / f.denominator + 1
}

// Continued-fraction approximation of (decimal - 1) into a tidy ratio.
export function decimalToFractional(decimalOdds: number, maxDenominator = 100): Fractional {
  const value = decimalOdds - 1
  if (value <= 0) return { numerator: 0, denominator: 1 }
  let h1 = 1
  let h0 = 0
  let k1 = 0
  let k0 = 1
  let x = value
  for (let i = 0; i < 64; i++) {
    const a = Math.floor(x)
    const h2 = a * h1 + h0
    const k2 = a * k1 + k0
    if (k2 > maxDenominator) break
    h0 = h1
    h1 = h2
    k0 = k1
    k1 = k2
    const frac = x - a
    if (frac < EPS) break
    x = 1 / frac
  }
  return { numerator: h1, denominator: k1 || 1 }
}

export function fractionalToImplied(f: Fractional): number {
  return decimalToImplied(fractionalToDecimal(f))
}

export function toDecimal(value: number | Fractional, format: OddsFormat): number {
  switch (format) {
    case 'decimal':
      return value as number
    case 'american':
      return americanToDecimal(value as number)
    case 'fractional':
      return fractionalToDecimal(value as Fractional)
  }
}

export function formatAmerican(american: number): string {
  if (american === 0) return 'EVEN'
  return american > 0 ? `+${american}` : `${american}`
}

export function formatFractional(f: Fractional): string {
  return `${f.numerator}/${f.denominator}`
}

export interface VigResult {
  fair: number[]
  overround: number
  marginPct: number
  method: VigMethod
}

export type VigMethod = 'proportional' | 'additive' | 'power'

export function marketOverround(impliedProbs: readonly number[]): number {
  let total = 0
  for (const p of impliedProbs) total += p
  return total
}

export function removeVig(
  impliedProbs: readonly number[],
  method: VigMethod = 'proportional',
): VigResult {
  const total = marketOverround(impliedProbs)
  const n = impliedProbs.length
  if (n === 0 || total <= 0) {
    return { fair: [...impliedProbs], overround: total, marginPct: 0, method }
  }

  let fair: number[]
  if (method === 'additive') {
    const excess = (total - 1) / n
    fair = impliedProbs.map((p) => clamp(p - excess, 0, 1))
  } else if (method === 'power') {
    fair = powerNoVig(impliedProbs)
  } else {
    fair = impliedProbs.map((p) => p / total)
  }

  const fairTotal = marketOverround(fair)
  if (fairTotal > 0) fair = fair.map((p) => p / fairTotal)

  return { fair, overround: total, marginPct: (total - 1) * 100, method }
}

// Solve k so that sum(p_i^k) = 1 (bisection); reflects favourite-longshot bias better than proportional.
function powerNoVig(impliedProbs: readonly number[]): number[] {
  let lo = 0.0001
  let hi = 10
  const f = (k: number) => impliedProbs.reduce((acc, p) => acc + Math.pow(p, k), 0) - 1
  for (let i = 0; i < 200; i++) {
    const mid = (lo + hi) / 2
    const val = f(mid)
    if (Math.abs(val) < 1e-10) {
      lo = mid
      hi = mid
      break
    }
    if (val > 0) lo = mid
    else hi = mid
  }
  const k = (lo + hi) / 2
  return impliedProbs.map((p) => Math.pow(p, k))
}

export function noVigTwoWay(decimalA: number, decimalB: number): { a: number; b: number } {
  const pa = decimalToImplied(decimalA)
  const pb = decimalToImplied(decimalB)
  const result = removeVig([pa, pb], 'proportional')
  return { a: result.fair[0], b: result.fair[1] }
}
